import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { renderDesign } from "../lib/renderDesign";
import { sampleFor } from "../lib/sampleValues";
import type { SerializableDesign } from "../lib/serializableDesign";

interface DesignPreviewProps {
  design: SerializableDesign;
  widgetType: string;
  cardWidth: number;
  cardHeight: number;
  className?: string;
}

/**
 * Renders a submitted design's SVG with sample widget data — the same engine
 * the live widget uses. The payload is sanitized server-side before it ever
 * reaches the backoffice, so inlining it is safe.
 */
const DesignPreview = ({
  design,
  widgetType,
  cardWidth,
  cardHeight,
  className,
}: DesignPreviewProps) => {
  const { t } = useTranslation("common");
  const html = useMemo(() => {
    try {
      return renderDesign(design, sampleFor(widgetType));
    } catch {
      return "";
    }
  }, [design, widgetType]);

  const aspectRatio =
    cardWidth > 0 && cardHeight > 0 ? `${cardWidth} / ${cardHeight}` : "16 / 9";

  if (!html) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-md border border-dashed border-neutral-800 bg-neutral-950 text-xs text-neutral-500",
          className,
        )}
        style={{ aspectRatio }}
      >
        {t("DESIGNS.previewUnavailable", "Preview unavailable")}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-neutral-800 bg-neutral-950 [&>svg]:block [&>svg]:h-full [&>svg]:w-full",
        className,
      )}
      style={{ aspectRatio }}
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default DesignPreview;
